/*
 * AYA — Service Worker registration + update flow
 * ---------------------------------------------------------------------------
 * - Enregistre /sw.js au boot (prod only par défaut, on peut forcer en dev)
 * - Détecte "beforeinstallprompt" pour l'install prompt A2HS (Android/Chrome)
 * - Détecte "appinstalled" pour marquer l'état installé
 * - Détecte une nouvelle version du SW (updatefound + statechange) et exécute
 *   le callback `onUpdateReady` (le composant UI affichera un toast "Recharger")
 * ---------------------------------------------------------------------------
 */

let deferredInstallPrompt = null;
const INSTALL_LISTENERS = new Set();
const UPDATE_LISTENERS = new Set();
let currentRegistration = null;

export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  // Écoute beforeinstallprompt AVANT registration (l'event peut arriver très vite)
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    INSTALL_LISTENERS.forEach((cb) => { try { cb('available'); } catch (_) { /* silent */ } });
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    INSTALL_LISTENERS.forEach((cb) => { try { cb('installed'); } catch (_) { /* silent */ } });
    try {
      
      const { markInstalled } = require('./valueSignals');
      markInstalled();
    } catch (_) { /* silent */ }
  });

  // Register après window.load pour ne pas bloquer le rendu critique
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then((reg) => {
        currentRegistration = reg;
        // Détecte update
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // Un nouveau SW est prêt à prendre le contrôle (waiting)
              UPDATE_LISTENERS.forEach((cb) => { try { cb(reg); } catch (_) { /* silent */ } });
            }
          });
        });
        // Vérifie immédiatement s'il y a déjà un SW en waiting
        if (reg.waiting && navigator.serviceWorker.controller) {
          UPDATE_LISTENERS.forEach((cb) => { try { cb(reg); } catch (_) { /* silent */ } });
        }
      })
      .catch((err) => {
        console.warn('[AYA PWA] SW registration failed:', err);
      });

    // Force reload quand le SW controller change (nouvelle version prise)
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

export function isInstallPromptAvailable() {
  return !!deferredInstallPrompt;
}

export async function triggerInstallPrompt() {
  if (!deferredInstallPrompt) return { outcome: 'unavailable' };
  const p = deferredInstallPrompt;
  deferredInstallPrompt = null;
  try {
    await p.prompt();
    const choice = await p.userChoice;
    return { outcome: choice.outcome }; // 'accepted' | 'dismissed'
  } catch (_) {
    return { outcome: 'error' };
  }
}

export function subscribeInstallEvents(cb) {
  INSTALL_LISTENERS.add(cb);
  return () => INSTALL_LISTENERS.delete(cb);
}

export function subscribeUpdateEvents(cb) {
  UPDATE_LISTENERS.add(cb);
  return () => UPDATE_LISTENERS.delete(cb);
}

export function acceptUpdate(reg) {
  const target = reg || currentRegistration;
  if (target?.waiting) {
    target.waiting.postMessage({ type: 'SKIP_WAITING' });
  } else {
    // Fallback : force reload
    window.location.reload();
  }
}

// Détecte l'environnement iOS (Safari) — pas de beforeinstallprompt là-bas
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iP(hone|ad|od)/.test(navigator.platform) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}
